import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import {
	$lastQueryKeyForPage,
	$queryExecutor,
	$queryState,
	pageKey,
	requestExecutionStateKey,
} from "../../internal/request-page";
import { usePageFromCurrentRoute } from "./usePageFromCurrentRoute";

type UseRequestExecutionParams = {
	documentId?: string;
	pageId?: string;
};

export const useRequestExecution = (params?: UseRequestExecutionParams) => {
	const { documentId: currentDocumentId, pageSlug, pageMetadata } = usePageFromCurrentRoute();
	const documentId = params?.documentId ?? currentDocumentId ?? "";
	const pageId = params?.pageId ?? pageMetadata?.id ?? pageSlug ?? "";
	const pageName = pageMetadata?.title ?? "";

	// Look up the last query key used for this page (could be from UI or code execution)
	// Fall back to the predictable UI key if no request has been run yet
	const lastKeyAtom = useMemo(
		() => $lastQueryKeyForPage(pageKey(documentId, pageId)),
		[documentId, pageId]
	);
	const lastKey = useAtomValue(lastKeyAtom);
	const key = lastKey ?? requestExecutionStateKey(documentId, pageId);

	const stateAtom = useMemo(() => $queryState(key), [key]);
	const state = useAtomValue(stateAtom);
	const dispatch = useSetAtom($queryExecutor(documentId));

	// Always use the predictable UI key when sending from the UI.
	// Spec-level Postgres params (Kv[]) are merged with caller params inside
	// $queryExecutor, so the hook doesn't need to extract them here.
	const uiKey = requestExecutionStateKey(documentId, pageId);
	const sendRequest = useCallback(async () => {
		await dispatch({ pageName, key: uiKey, params: {}, pageId });
	}, [dispatch, pageName, uiKey, pageId]);

	const abortRequest = useCallback(() => {
		// No-op: let requests complete in background and cache the result
	}, []);

	const reset = useCallback(() => {
		// No-op: unused by UI
	}, []);

	if (!documentId || !pageId) {
		return {
			documentId,
			pageId,
			response: undefined,
			timing: undefined,
			error: null,
			isExecuting: false,
			sendRequest,
			abortRequest,
			reset,
		};
	}

	return {
		documentId,
		pageId,
		response: state.data,
		timing: state.timing,
		error: state.error ?? null,
		isExecuting: state.fetchStatus === "fetching",
		sendRequest,
		abortRequest,
		reset,
	};
};
